import cx from 'classnames';
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { selectAddOnTotal } from '../features/addOns/addOnsSlice';
import {
  clearStoredDiscounts,
  setStoredDiscountAmount,
  setStoredDiscountCodeId,
  setStoredDiscountType,
  setStoredPromoCreditAmount,
  selectStoredDiscountAmount,
  selectStoredDiscountType,
  selectStoredDiscountCodeId,
  selectStoredPromoCreditAmount,
  selectSlotPrice,
  selectPaymentPreference,
  selectPrepayDiscountPercent,
  selectTaxRatePercent,
  selectPlanStartFeeIncluded,
  selectBestSlotAdjustmentLabel,
  selectBestSlotAdjustmentPercent,
} from '../features/booking/bookingSlice';
import {
  formatCents,
  isMobileServiceFee,
  JOIN_FEE_WAIVER,
  PaymentPreference,
  prepayDiscountCents,
  SlotPriceTier,
} from '../util/bookingPricing';
import {
  selectServicesTotal,
} from '../features/serviceTypes/serviceTypesSlice';
import styles from '../sass/components/Invoice.module.scss';
import DiscountCodeInput from './DiscountCodeInput';
import {
  additionalFeeNames,
  discountedFeesNames,
} from '../constants/discountedFees';
import { GET_USER_CREDIT } from '../util/gql';
import CreditInput from './CreditInput';
import { useTenant } from './TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
import { aperioPlanLabel } from '../util/aperioPlanCopy';
import { serviceNameText, untranslatedLang } from '../util/serviceText';
import {
  formatDeduction,
  formatMoney,
  formatNumber,
} from '../i18n/format';
import {
  isServiceNameKey,
  isSubscribeTo,
  isSubscriptionPlan,
  ServiceNameKeys,
} from '../util/serviceKeys';

interface InvoiceProps {
  className?: string;
  showDisclaimer?: boolean;
  condense?: boolean;
  onlyServices?: boolean;
  includeAddOns?: boolean;
  onDiscountUpdate?: (
    discountAmount: number | null,
    discountType: string | null,
    discountFeesAmount: number,
    discointCodeId?: string,
  ) => void;
  isVendor?: boolean;
  email?: string;
}

export default function Invoice({
  className,
  showDisclaimer = true,
  condense,
  onlyServices = false,
  includeAddOns = false,
  onDiscountUpdate,
  isVendor = false,
  email,
}: InvoiceProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Aperio: no per-month figure for the annual plan (the booking has no
  // monthly payment option), no $0.00 fee lines, and payment wording that
  // matches the chosen way to pay. Every other tenant renders as before.
  const aperio = normalizeThemePreset(useTenant()?.themePreset) === 'aperio';
  const currentUser = useAppSelector((
    state,
  ) => state.auth.currentUser);
  const isLoggedIn = useAppSelector((state) => state.auth.isAuthenticated);
  const carId = useAppSelector((state) => state.car.id);
  const customerId = useAppSelector((state) => state.customer.id);
  const isDsp = useAppSelector(
    (state) => state.franchise.type === 2,
  );

  const userId = customerId || currentUser.id;
  const storedDiscountAmount = useAppSelector(
    selectStoredDiscountAmount,
  );
  const storedDiscountType = useAppSelector(
    selectStoredDiscountType,
  );
  const storedDiscountCodeId = useAppSelector(
    selectStoredDiscountCodeId,
  );
  const storedPromoCreditAmount = useAppSelector(
    selectStoredPromoCreditAmount,
  );
  const slotPrice = useAppSelector(selectSlotPrice);
  const paymentPreference = useAppSelector(selectPaymentPreference);
  const prepayDiscountPercent = useAppSelector(selectPrepayDiscountPercent);
  const taxRatePercent = useAppSelector(selectTaxRatePercent);
  const bestSlotAdjustmentLabel = useAppSelector(selectBestSlotAdjustmentLabel);
  const bestSlotAdjustmentPercent = useAppSelector(
    selectBestSlotAdjustmentPercent,
  );
  const [discountCode, setDiscountCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [discountAmount, setDiscountAmount] = useState<number | null>(null);
  const [discountPercentAmount, setDiscountPercentAmount] = useState(0);
  const [discountType, setDiscountType] = useState<string | null>(null);
  const [
    appliedDiscountCodeId,
    setAppliedDiscountCodeId,
  ] = useState<string | null>();
  const [prevEmail, setPrevEmail] = useState(email);
  const [creditErrorMessage, setCreditErrorMessage] = useState('');
  const [promoCreditAmount,
    setPromoCreditAmount] = useState<number | null>(null);

  const {
    data: creditData,
  } = useQuery(GET_USER_CREDIT, {
    variables: { userId },
    skip: !userId || !showDisclaimer,
    fetchPolicy: 'network-only',
  });

  const availableCredit = creditData?.getUserCredit?.credit ?? 0;
  const shouldShowCreditInput = availableCredit > 0
  && promoCreditAmount === null
  && showDisclaimer;

  // Held in a ref so the reset below runs only when isVendor or email
  // change, not whenever the parent passes a new callback identity. A code
  // is checked against the email it was applied with, so a new email
  // clears it.
  const onDiscountUpdateRef = useRef(onDiscountUpdate);
  onDiscountUpdateRef.current = onDiscountUpdate;

  useEffect(() => {
    if (isVendor || (email && email !== prevEmail)) {
      dispatch(clearStoredDiscounts());
      if (onDiscountUpdateRef.current) {
        onDiscountUpdateRef.current(0, null, 0, undefined);
      }
    }
    if (email !== prevEmail) {
      setPrevEmail(email);
    }
  }, [isVendor, email, prevEmail, dispatch]);

  const setServiceValue = (
    value: number, discountedValue?: number,
  ) => (
    discountedValue !== null
    && discountedValue !== undefined
    && Number.isFinite(discountedValue)
      ? discountedValue
      : value
  );

  const selectedServices = (useAppSelector((state) => (
    state.serviceTypes.services
  )))
    .map((selectedService) => ({
      ...selectedService,
      value: setServiceValue(
        selectedService.value,
        selectedService.discountedValue,
      ),
    }));

  // Subscription bookings carry two services: the onboarding fee (the card
  // the customer picked) and the hidden plan. Present them as one block.
  const isPlanService = (s: { nameKey?: string | null }) => (
    isSubscriptionPlan(s.nameKey)
    || s.nameKey === ServiceNameKeys.subscriptionPlanAnnualAllInclusive
  );
  const subscriptionOnboarding = selectedServices.find(
    (s) => isSubscribeTo(s.nameKey),
  );
  const subscriptionPlan = selectedServices.find(isPlanService);
  const regularServices = selectedServices.filter(
    (s) => !isSubscribeTo(s.nameKey) && !isPlanService(s),
  );
  const planIsMonthly = subscriptionPlan?.nameKey
    === ServiceNameKeys.subscriptionPlanMonthly;

  const selectedServicesForDiscount = selectedServices
    .map((selectedService) => ({
      value: setServiceValue(
        selectedService.value,
        selectedService.discountedValue,
      ),
      name: selectedService.name,
      id: selectedService.id,
      nameKey: selectedService.nameKey,
      serviceTypeName: selectedService.serviceTypeName,
    }));

  const selectedAddOns = (useAppSelector((state) => state.addOns.addOns) || [])
    .map((selectedAddOn) => ({
      ...selectedAddOn,
      value: setServiceValue(selectedAddOn.value,
        selectedAddOn.discountedValue),
    }));

  // A plan start whose onboarding includes the visit's mobile fee (Aperio):
  // that $0 fee is listed as included, not hidden like other $0 fees.
  const planStartFeeIncluded = useAppSelector(selectPlanStartFeeIncluded);
  const isIncludedFee = (addOn: typeof selectedAddOns[number]) => (
    planStartFeeIncluded && addOn.value === 0 && isMobileServiceFee(addOn)
  );
  const additionalFees = selectedAddOns.filter(
    (addOn) => isServiceNameKey(addOn.nameKey)
    && additionalFeeNames.includes(addOn.nameKey)
    && !(aperio && addOn.value === 0 && !isIncludedFee(addOn)),
  );

  const separateAddOns = selectedAddOns.filter(
    (addOn) => !(isServiceNameKey(addOn.nameKey)
    && additionalFeeNames.includes(addOn.nameKey)),
  );

  const discountedFees = selectedAddOns.filter(
    (addOn) => isServiceNameKey(addOn.nameKey)
    && discountedFeesNames.includes(addOn.nameKey),
  );

  const serviceTotal = useAppSelector((
    state,
  ) => selectServicesTotal(state));

  const addOnTotal = useAppSelector((state) => selectAddOnTotal(state));

  const make = useAppSelector((state) => (
    state.car.make
  ));
  const model = useAppSelector((state) => (
    state.car.model
  ));
  const year = useAppSelector((state) => (
    state.car.year
  ));

  let totalDiscountAmount = 0;
  let discountOnFees = 0;

  if (discountAmount) {
    if (discountType === 'PERCENT') {
      totalDiscountAmount = discountPercentAmount;
    } else if (discountType === 'PROMO' || discountType === JOIN_FEE_WAIVER) {
      totalDiscountAmount = discountAmount;
      discountOnFees = 0;
    } else {
      discountOnFees = discountedFees.reduce(
        (total, fee) => total + fee.value, 0,
      );
      totalDiscountAmount = discountAmount + discountOnFees;
    }
  }

  // Airline-style adjustment for the chosen appointment time (cents, signed),
  // and the prepay discount on the pre-tax subtotal including that adjustment.
  const slotAdjustmentDollars = (slotPrice?.cents ?? 0) / 100;
  const preTaxSubtotalCents = Math.round((addOnTotal + serviceTotal) * 100)
    + (slotPrice?.cents ?? 0);
  const prepayDiscountDollars = paymentPreference === PaymentPreference.prepay
    ? prepayDiscountCents(preTaxSubtotalCents, prepayDiscountPercent) / 100
    : 0;

  const rawTotal = addOnTotal
  + serviceTotal
  + slotAdjustmentDollars
  + prepayDiscountDollars
  - totalDiscountAmount
  - (promoCreditAmount || 0);
  const finalTotal = Math.max(0, rawTotal);
  const totalAmount = formatMoney(finalTotal);
  // Estimated sales tax for the service address, so the summary can show a
  // real total instead of "plus applicable taxes".
  const estimatedTax = taxRatePercent !== null
    ? Math.round(finalTotal * taxRatePercent) / 100
    : null;
  const estimatedTotal = estimatedTax !== null
    ? formatMoney(finalTotal + estimatedTax)
    : null;

  // The rule that priced the chosen time, when the server names it.
  const slotRuleLabel = useAppSelector(
    (state) => state.serviceLocation?.dateTime?.priceAdjustmentLabel,
  );
  let slotAdjustmentLabel: string | null = null;
  if (slotPrice && slotPrice.cents < 0 && slotRuleLabel) {
    slotAdjustmentLabel = t('invoice.ruleDiscount', { rule: slotRuleLabel });
  } else if (slotPrice && slotPrice.cents < 0
    && slotRuleLabel === undefined) {
    // Named after the rule that gave it when that is the tenant's best
    // discount rule (e.g. "Book 14+ days ahead"): a -15% for booking early
    // was labelled "Off-peak booking discount".
    slotAdjustmentLabel = bestSlotAdjustmentLabel
      && bestSlotAdjustmentPercent < 0
      && slotPrice.percent === bestSlotAdjustmentPercent
      ? t('invoice.ruleDiscount', { rule: bestSlotAdjustmentLabel })
      : t('invoice.offPeak');
  } else if (slotPrice && slotPrice.cents < 0) {
    // The server says no lead-time rule applied (an off-peak time, a
    // weekday): never "Book 14+ days ahead" for it.
    slotAdjustmentLabel = t('invoice.offPeak');
  } else if (slotPrice && slotPrice.cents > 0) {
    slotAdjustmentLabel = t('invoice.peak');
  }

  useEffect(() => {
    setDiscountAmount(storedDiscountAmount ?? null);
    setDiscountType(storedDiscountType ?? null);
    setAppliedDiscountCodeId(storedDiscountCodeId ?? null);
    setPromoCreditAmount(storedPromoCreditAmount ?? null);
  }, [storedDiscountAmount,
    storedDiscountType,
    storedDiscountCodeId,
    storedPromoCreditAmount]);

  useEffect(() => {
    if (discountAmount === null) {
      setDiscountPercentAmount(0);
      if (onDiscountUpdate) {
        onDiscountUpdate(
          null,
          null,
          0,
          appliedDiscountCodeId || undefined,
        );
      }
      return;
    }
    if (discountType === 'PERCENT') {
      const percent = discountAmount / 10000;
      const calculatedDiscountPercentAmount = (serviceTotal + addOnTotal)
      * percent;
      setDiscountPercentAmount(calculatedDiscountPercentAmount);
      if (onDiscountUpdate) {
        onDiscountUpdate(
          calculatedDiscountPercentAmount,
          discountType,
          0,
          appliedDiscountCodeId || undefined,
        );
      }
    } else {
      setDiscountPercentAmount(0);
      if (onDiscountUpdate) {
        onDiscountUpdate(
          discountAmount,
          discountType,
          discountOnFees,
          appliedDiscountCodeId || undefined,
        );
      }
    }
  }, [serviceTotal,
    addOnTotal,
    discountType,
    appliedDiscountCodeId,
    dispatch,
    discountAmount,
    onDiscountUpdate,
    discountOnFees]);

  const handleApplyCredit = (amount: number) => {
    const amountInDollars = amount / 100;
    setPromoCreditAmount(amountInDollars);
    dispatch(setStoredPromoCreditAmount(amountInDollars));
  };

  const handleDiscountApplied = (
    newDiscountAmount: number | null,
    type: string,
    discountCodeId?: string,
  ) => {
    if (newDiscountAmount === null) {
      setDiscountAmount(null);
      setDiscountType(null);
      setAppliedDiscountCodeId(undefined);
      dispatch(setStoredDiscountAmount(null));
      dispatch(setStoredDiscountType(null));
      dispatch(setStoredDiscountCodeId(null));
      if (onDiscountUpdate) {
        onDiscountUpdate(null, null, 0, undefined);
      }
      return;
    }
    setDiscountType(type);
    dispatch(setStoredDiscountType(type));
    dispatch(setStoredDiscountCodeId(discountCodeId || null));
    if (type === 'PERCENT') {
      setDiscountAmount(newDiscountAmount);
      dispatch(setStoredDiscountAmount(newDiscountAmount));
      if (onDiscountUpdate) {
        onDiscountUpdate(
          newDiscountAmount,
          type,
          0,
          discountCodeId,
        );
      }
    } else {
      const recalculatedDiscountAmount = newDiscountAmount / 100;
      setDiscountAmount(recalculatedDiscountAmount);
      dispatch(setStoredDiscountAmount(recalculatedDiscountAmount));
      if (onDiscountUpdate) {
        onDiscountUpdate(
          recalculatedDiscountAmount,
          type,
          discountOnFees,
          discountCodeId,
        );
      }
    }
  };

  // Every customer may enter a code, logged in or not (a guest's is checked
  // by the email on the Schedule step); vendor (fleet) bookings never do.
  const shouldShowDiscountInput = discountAmount === null
  && !isVendor && showDisclaimer;

  return (
    <div key="invoice" className={cx(styles.invoice, className)}>
      <div
        key="summary"
        className={cx(styles.summarySection, styles.card, {
          [styles.condenseSections]: condense,
        })}
      >
        { !!selectedServices.length && (
          <div key="services" className={styles.cardSection}>
            {!onlyServices
              && (
              <span
                className={cx('label', styles.cardHeader)}
              >
                {t('invoice.services')}
              </span>
              )}
            <div className={cx('label', styles.cardContent)}>
              {subscriptionPlan && (
                <div className={styles.subscriptionBlock}>
                  <div className={styles.lineItem}>
                    <p>
                      &bull;
                      &nbsp;
                      &nbsp;
                      {(aperio && aperioPlanLabel(subscriptionPlan.nameKey))
                        || subscriptionPlan.name}
                      <span className={styles.lineItemHint}>
                        {' '}
                        {(() => {
                          if (planIsMonthly) {
                            const count = subscriptionPlan.planPriceBreakdown
                              ?.instalmentCount;
                            return aperio && count
                              ? t('invoice.paymentOneOf', { count })
                              : t('invoice.billedMonthly');
                          }
                          if (aperio) {
                            // Keeps the surcharge explanation (e.g.
                            // "European make: +$60.00"), never a /mo figure.
                            return subscriptionPlan.priceNote
                              ? t('invoice.upfrontNote', {
                                note: subscriptionPlan.priceNote,
                              })
                              : t('invoice.upfront');
                          }
                          return `(12 months, paid upfront · $${
                            (subscriptionPlan.value / 12).toFixed(2)
                          }/mo)`;
                        })()}
                      </span>
                    </p>
                    <p>{formatMoney(subscriptionPlan.value)}</p>
                  </div>
                  {subscriptionOnboarding && (
                    <div className={styles.lineItem}>
                      <p>
                        &bull;
                        &nbsp;
                        &nbsp;
                        {t('invoice.onboardingFee')}
                      </p>
                      <p>{formatMoney(subscriptionOnboarding.value)}</p>
                    </div>
                  )}
                </div>
              )}
              {
                regularServices.map((service) => {
                  const label = serviceNameText(service);
                  return (
                    <div key={service.id} className={styles.lineItem}>
                      <p>
                        &bull;
                        &nbsp;
                        &nbsp;
                        {untranslatedLang(label.isTranslated) ? (
                          <span lang="en">{label.text}</span>
                        ) : label.text}
                      </p>
                      <p>{formatMoney(service.value)}</p>
                    </div>
                  );
                })
              }
            </div>
          </div>
        )}
        { (!onlyServices || includeAddOns) && !!separateAddOns.length && (
          <div key="addons" className={styles.cardSection}>
            <span className={cx('label', styles.cardHeader)}>
              {t('invoice.addOns')}
            </span>
            <div className={cx('label', styles.cardContent)}>
              {
                separateAddOns.map((addOn) => {
                  const label = serviceNameText(addOn);
                  return (
                    <div key={addOn.name} className={styles.lineItem}>
                      <p>
                        &bull;
                        &nbsp;
                        &nbsp;
                        {untranslatedLang(label.isTranslated) ? (
                          <span lang="en">{label.text}</span>
                        ) : label.text}
                      </p>
                      <p>{formatMoney(addOn.value)}</p>
                    </div>
                  );
                })
              }
            </div>
          </div>
        )}
        { !onlyServices && !!additionalFees.length && (
          <div key="additional-fees" className={styles.cardSection}>
            <span className={cx('label', styles.cardHeader,
              styles.additionalFees)}
            >
              {t('invoice.additionalFees')}
            </span>
            <div className={cx('label', styles.cardContent,
              styles.additionalFees)}
            >
              {additionalFees.map((addOn) => (
                <div key={addOn.name} className={styles.lineItem}>
                  <p>
                    &bull; &nbsp;
                    {' '}
                    {aperio && addOn.nameKey === ServiceNameKeys.mileageCharge
                      // Named and explained where it first appears (QA #21:
                      // an unexplained "Mileage Charge $0.17").
                      ? (
                        <>
                          {t('invoice.travelFee')}
                          <span className={styles.lineItemHint}>
                            {t('invoice.travelFeeHint')}
                          </span>
                        </>
                      )
                      : serviceNameText(addOn).text}
                  </p>
                  <p>
                    {isIncludedFee(addOn)
                      ? t('invoice.includedWithOnboarding')
                      : formatMoney(addOn.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {!onlyServices ? (
        <div
          key="totals"
          className={cx(styles.summarySection, {
            [styles.condenseSections]: condense,
            [styles.excludeHeaders]: onlyServices,
          })}
        >
          {shouldShowDiscountInput && (
          <DiscountCodeInput
            discountCode={discountCode}
            setDiscountCode={setDiscountCode}
            selectedServices={selectedServicesForDiscount}
            carId={carId}
            userId={userId}
            email={email}
            guest={!isLoggedIn}
            isDsp={isDsp}
            onDiscountApplied={handleDiscountApplied}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
          />
          )}

          {discountAmount === 0 && discountType === 'PROMO' && (
          <div
            key="gooil-discount"
            className={cx(styles.lineItem, styles.noPaddingLeft)}
          >
            <span className="label">{t('invoice.promoApplied')}</span>
          </div>
          )}

          {!!discountAmount && (
          <div>
            {discountType === 'PROMO' && (
            <div
              key="promo-discount"
              className={cx(styles.lineItem, styles.noPaddingLeft)}
            >
              <span className="label">{t('invoice.promoApplied')}</span>
              <span>{formatDeduction(discountAmount)}</span>
            </div>
            )}

            {discountType === JOIN_FEE_WAIVER && (
            <div
              key="join-fee-waiver"
              className={cx(styles.lineItem, styles.noPaddingLeft)}
            >
              <span className="label">{t('invoice.joinFeeWaived')}</span>
              <span>{formatDeduction(discountAmount)}</span>
            </div>
            )}

            {discountType === 'PERCENT' && (
            <div
              key="gooil-discount"
              className={cx(styles.lineItem, styles.noPaddingLeft)}
            >
              <span className="label">
                {aperio ? t('invoice.discount') : 'Gooil Discount'}
              </span>
              <span>{formatDeduction(discountPercentAmount)}</span>
            </div>
            )}

            {!['PERCENT', 'PROMO', JOIN_FEE_WAIVER].includes(discountType ?? '') && (
            <>
              <div
                key="groupon-discount"
                className={cx(styles.lineItem, styles.noPaddingLeft)}
              >
                <span className="label">Groupon Discount (on Services)</span>
                <span>{formatDeduction(discountAmount)}</span>
              </div>
              {discountOnFees > 0 && (
              <div
                key="groupon-discount-fees"
                className={cx(styles.lineItem, styles.noPaddingLeft)}
              >
                <span className="label">Groupon Discount (on Fees)</span>
                <span>{formatDeduction(discountOnFees)}</span>
              </div>
              )}
            </>
            )}
          </div>
          )}

          {promoCreditAmount != null && (
          <div
            key="referral-credit"
            className={cx(styles.lineItem, styles.noPaddingLeft)}
          >
            <span className="label">{t('invoice.referralCredit')}</span>
            <span>{formatDeduction(promoCreditAmount)}</span>
          </div>
          )}

          {slotAdjustmentLabel && slotPrice && (
          <div
            key="slot-price-adjustment"
            className={cx(styles.lineItem, styles.noPaddingLeft, {
              [styles.savings]: slotPrice.cents < 0,
              [styles.surcharge]: slotPrice.cents > 0,
            })}
          >
            <span className="label">
              {slotAdjustmentLabel}
              {slotPrice.percent !== 0 && (
                <span className={styles.lineItemHint}>
                  {' '}
                  {t('invoice.forThisTime', {
                    percent: `${slotPrice.percent > 0 ? '+' : ''}${
                      slotPrice.percent}`,
                  })}
                </span>
              )}
            </span>
            <span>
              {slotPrice.cents < 0 ? '- ' : '+ '}
              {formatCents(slotPrice.cents)}
            </span>
          </div>
          )}

          {prepayDiscountDollars !== 0 && (
          <div
            key="prepay-discount"
            className={cx(
              styles.lineItem,
              styles.noPaddingLeft,
              styles.savings,
            )}
          >
            <span className="label">
              {t('invoice.prepayDiscount')}
              <span className={styles.lineItemHint}>
                {' '}
                {t('invoice.percentOff', { percent: prepayDiscountPercent })}
              </span>
            </span>
            <span>{formatDeduction(Math.abs(prepayDiscountDollars))}</span>
          </div>
          )}

          {slotPrice?.tier === SlotPriceTier.peak && showDisclaimer && (
          <div className={cx(styles.lineItem, styles.noPaddingLeft)}>
            <span className={styles.lineItemHint}>
              {t('invoice.avoidPeak')}
            </span>
          </div>
          )}

          {shouldShowCreditInput && (
          <CreditInput
            availableCredit={availableCredit}
            appliedCredit={promoCreditAmount}
            setAppliedCredit={handleApplyCredit}
            errorMessage={creditErrorMessage}
            setErrorMessage={setCreditErrorMessage}
          />
          )}

          <div key="total" className={cx(styles.lineItem, styles.totalLine)}>
            <span className="label">
              {estimatedTax !== null ? t('invoice.subtotal') : t('invoice.total')}
            </span>
            <span>
              {totalAmount}
              { (!make || !model || !year) && '*' }
            </span>
          </div>
          {estimatedTax !== null && (
          <div key="tax" className={styles.lineItem}>
            <span>
              {t('invoice.estTax', { percent: formatNumber(taxRatePercent ?? 0) })}
            </span>
            <span>{formatMoney(estimatedTax)}</span>
          </div>
          )}
          {estimatedTotal !== null && (
          <div key="est-total" className={cx(styles.lineItem, styles.totalLine)}>
            <span className="label">{t('invoice.estTotal')}</span>
            <span>
              {estimatedTotal}
              { (!make || !model || !year) && '*' }
            </span>
          </div>
          )}
          {(!make || !model || !year) && showDisclaimer && (
          <div className={styles.lineItem}>
            <span>
              <span className="label">
                {t('invoice.mayBeHigher')}
                &nbsp;
              </span>
              {t('invoice.baseline')}
              {estimatedTax === null && (
                <span>{t('invoice.taxNotIncluded')}</span>
              )}
            </span>
          </div>
          )}
          {showDisclaimer && aperio && (
          <div className={cx(styles.lineItem, styles.paymentNote)}>
            <span>
              {paymentPreference === PaymentPreference.prepay
                ? (
                  <>
                    <span className="label">
                      {t('invoice.prepayLabel', {
                        percent: prepayDiscountPercent,
                      })}
                      &nbsp;
                    </span>
                    {t('invoice.prepayBody')}
                  </>
                )
                : (
                  <>
                    <span className="label">
                      {t('invoice.noPaymentToday')}
                      &nbsp;
                    </span>
                    {t('invoice.payAfter')}
                  </>
                )}
            </span>
          </div>
          )}
          {showDisclaimer && !aperio && (
          <div className={cx(styles.lineItem, styles.paymentNote)}>
            <span>
              <span className="label">No payment required to book.&nbsp;</span>
              {paymentPreference === PaymentPreference.prepay
                ? `Prepay & save ${prepayDiscountPercent}%: you pay by card `
                  + 'on the next screen, right after locking in your booking.'
                : 'You pay after your service is complete.'}
            </span>
          </div>
          )}
        </div>
      ) : (
        <div key="hint" className={styles.summarySection}>
          <p>{t('invoice.totalNextStep')}</p>
        </div>
      )}
    </div>
  );
}
